export const appendParamObjectToURL = (url, params) => {
  return appendQueryStringToURL(url, objToQueryString(params));
};

export const appendQueryStringToURL = (url, queryString) => {
  if (queryString === "") return url;

  const separator = url.includes("?") ? "&" : "?";

  return `${url}${separator}${queryString}`;
};

// If any key in the `defaults` object doesn't exist in `target`, the key is
// merged. Additional `sources` object arguments are merged into `target`, but
// only the keys that exist in `defaults`.
export const assignWithDefaults = (target, defaults, ...sources) => {
  Object.keys(defaults).forEach((key) => {
    if (!target.hasOwnProperty(key) || target[key] === undefined) {
      target[key] = defaults[key];
    }

    sources
      .filter((source) => {
        return (
          source != null &&
          source.hasOwnProperty(key) &&
          source[key] !== undefined
        );
      })
      .forEach((source) => (target[key] = source[key]));
  });

  return target;
};

/**
 * Takes an ordered array and returns the first value where fn(value) returns true.
 */
export const binaryFind = (arr, fn) => {
  let high = arr.length + 1;
  let low = 0;
  while (low + 1 < high) {
    const mid = low + ((high - low) >> 1);
    if (fn(arr[mid])) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return arr[high];
};

export const camelCase = (str, { isSuffix = false } = {}) => {
  return str
    .split(/[-_]/g)
    .map((part, i) =>
      i === 0 && !isSuffix ? part.toLowerCase() : capitalize(part.toLowerCase())
    )
    .join("");
};

export const capitalize = (str) => {
  if (str === "") return str;

  return `${str[0].toUpperCase()}${str.slice(1)}`;
};

export const clamp = (x, a, b) => {
  if (a > b) {
    return clamp(x, b, a);
  }

  if (x < a) {
    return a;
  }

  if (x > b) {
    return b;
  }

  return x;
};

export const clone = (obj) => {
  if (obj === undefined) return undefined;

  return JSON.parse(JSON.stringify(obj));
};

export const compareNumbers = (a, b) => {
  if (a == null && b == null) return 0;
  else if (a == null) return -1;
  else if (b == null) return 1;

  return a - b;
};

export const compareStrings = (a, b) => {
  return (a || "")
    .trim()
    .toLowerCase()
    .localeCompare((b || "").trim().toLowerCase());
};

/**
 * Composes functions such that any returned Promise is resolved before the next invocation.
 * @param  {Function[]} funcs - A list of potentially async functions that each take a single param. Any null/
 * undefined values are ignored.
 */
export const composeAsync = (...funcs) => {
  return (arg) => {
    let res = Promise.resolve(arg);
    for (let i = funcs.length - 1; i >= 0; i--) {
      const f = funcs[i];
      if (f == null) {
        continue;
      }
      res = res.then((arg) => f(arg));
    }
    return res;
  };
};

export const smartCompare = (a, b, { reverse = false } = {}) => {
  if (a === b) return 0;
  if (type(a) !== type(b) && b != null && a != null) {
    throw new Error(
      `Don't know how to compare type ${type(a)} with type ${type(b)}!`
    );
  }
  const compareType = a != null ? type(a) : type(b);

  const [valueA, valueB] = reverse ? [b, a] : [a, b];
  switch (compareType) {
    case "string":
      return compareStrings(valueA, valueB);
    case "number":
      return compareNumbers(valueA, valueB);
    default:
      // TODO(oscar): Add more types for comparison such as Dates.
      throw new Error(`Missing method for comparing type ${compareType}`);
  }
};

/**
 * Recursively compares 2 JS objects.
 * @param {any} a
 * @param {any} b
 * @param {Object} options
 * @param {boolean} options.toJSON - Uses clone() in order to compare objects as
 * their JSON representations.
 * Useful when comparing plain objects to class instances.
 */
export const deepEqual = (a, b, { toJSON = false } = {}) => {
  if (a === b) {
    return true;
  }

  const compareA = toJSON ? clone(a) : a;
  const compareB = toJSON ? clone(b) : b;
  const tA = type(compareA);
  const tB = type(compareB);

  if (tA === "array" && tB === "array") {
    if (compareA.length !== compareB.length) return false;

    for (let i = 0; i < compareA.length; i++) {
      if (!deepEqual(compareA[i], compareB[i])) return false;
    }

    return true;
  }

  if (tA === "set" && tB === "set") {
    if (compareA.size !== compareB.size) return false;

    const bShallowCopy = new Set(compareB);
    aLoop: for (const aElement of compareA) {
      // First check if there's an equivalent reference in the two sets before checking for deep equality,
      // since the latter is very inefficient.
      if (bShallowCopy.has(aElement)) {
        bShallowCopy.delete(aElement);
        continue;
      }

      for (const bElement of bShallowCopy) {
        if (deepEqual(aElement, bElement)) {
          bShallowCopy.delete(bElement);
          continue aLoop;
        }
      }

      return false;
    }

    return true;
  }

  if (tA === "object" && tB === "object") {
    for (const k in compareA) {
      if (!(k in compareB)) return false;
    }

    for (const k in compareB) {
      if (!(k in compareA) || !deepEqual(compareA[k], compareB[k]))
        return false;
    }

    return true;
  }

  return compareA === compareB;
};

export const deepInclude = (parent, child) => {
  if (type(parent.some) === "function") {
    return parent.some((obj) => deepEqual(obj, child));
  } else if (type(parent) === "object") {
    return Object.values(parent).some((obj) => deepEqual(obj, child));
  }
  return false;
};

export const deepUnique = (arr) => {
  let index = 0;
  while (index < arr.length) {
    const item = arr[index];
    arr = arr
      .slice(0, index + 1)
      .concat(
        arr.slice(index).filter((currItem) => !deepEqual(item, currItem))
      );
    index += 1;
  }

  return arr;
};

/**
 * @param {Object} a
 * @param {Object} b
 * @param {boolean} includeAKeys
 * @returns {Object} - An object containing keys in `b` if the values differ
 * from those in `a`. If `includeAKeys` is true, the keys in `a` that are NOT in
 * `b` are also included.
 */
export const diffObjects = (a, b, includeAKeys = false) => {
  const result = {};
  for (const k in b) {
    if (!deepEqual(a[k], b[k])) {
      result[k] = b[k];
    }
  }
  if (includeAKeys) {
    for (const k in a) {
      if (!(k in b)) {
        result[k] = null;
      }
    }
  }

  return result;
};

// https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
export const escapeRegExp = (str) => {
  return str.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
};

export const flatten = (arr) => {
  return [].concat.apply([], arr);
};

/**
 * Takes a list of words and joins them via coordinating conjunction grammar rules. Oxford comma is mandatory.
 * @param {String[]} words
 */
export const formatConjunction = (words, conjunction = "and") => {
  const wc = words.length;
  if (wc <= 2) {
    return words.join(` ${conjunction} `);
  } else {
    return [...words.slice(0, wc - 1), `${conjunction} ${words[wc - 1]}`].join(
      ", "
    );
  }
};

export const formatFileSize = (size) => {
  // NOTE: Math.log10 is not available in every browser and Math.log / Math.LN10
  // is unreliable.
  const thousands = Math.floor((`${size}`.length - 1) / 3);
  return `${Math.round(size / Math.pow(10, 3 * thousands))}${
    "BKMGTP"[thousands]
  }`;
};

export const formatUSD = (val, options = {}) => {
  options = Object.assign(
    {
      style: "currency",
      currency: "USD",
    },
    options
  );

  return type(val) !== "number" ? null : val.toLocaleString("en", options);
};

export const getLocale = () => {
  const { navigator } = window;
  if (navigator == null) return null;
  return navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;
};

/**
 * This function is a wrapper for Number.toLocalString() however it tries to use
 * the device's language. If all else fails, it uses "en".
 */
export const numberToLocaleString = (n, options, locale) => {
  const f = (locale) => {
    try {
      return Number(n).toLocaleString(locale, options);
    } catch (e) {}
  };
  return f(locale) || f() || f(getLocale()) || f("en");
};

export const getIn = (m, ks, d) => {
  if (m == null) return d;
  if (ks.length === 1) {
    return m[ks[0]];
  }

  return getIn(m[ks[0]], ks.slice(1));
};

export const kebabCase = (str) => {
  // A "-" will be inserted before every capital letter, so make sure the first
  // character isn't capitalized.
  str = str.charAt(0).toLowerCase() + str.slice(1);
  // Convert camel case to kebab case.
  str = str.replace(/[A-Z]/g, "-$&").toLowerCase();
  // Convert snake case to kebab case.
  str = str.replace(/_/g, "-");

  return str;
};

export const isPromise = (obj) => {
  return Promise.resolve(obj) === obj;
};

export const isEmpty = (obj) =>
  obj == null ||
  (Object.keys(obj).length === 0 &&
    // Needed for iterables like `Set`.
    Array.from(obj).length === 0 &&
    (obj.size || 0) === 0);

export const loadImage = (url, crossOrigin = null) => {
  if (!url) return Promise.reject(new Error("No image URL provided"));

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = crossOrigin;
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
    if (img.complete) {
      resolve(img);
    }
  });
};

export const loadImages = (urls, crossOrigin = null) =>
  Promise.all(
    urls.filter((url) => url != null).map((url) => loadImage(url, crossOrigin))
  );

export const mapByID = (arr, fn = null) => {
  return arr.reduce((map, item) => {
    map[item.id] = fn != null ? fn(item) : item;
    return map;
  }, {});
};

export const mapcat = (arr, fn) => {
  return [].concat.apply([], arr.map(fn));
};

// Like `Object.assign`, merges subsequent object arguments into the first
// object argument `defaults`, but only keys that exist in `defaults`.
// Unlike `Object.assign`, does not modify the first argument, but returns a
// shallow clone instead.
export const mergePropertiesWithDefaults = (defaults, ...args) => {
  if (args.length === 0) return defaults;

  const target = Object.assign(defaults);
  const keys = Object.keys(target);

  args.forEach((source) => {
    if (source == null) return;

    keys.forEach((key) => {
      if (source.hasOwnProperty(key) && source[key] !== undefined) {
        target[key] = source[key];
      }
    });
  });

  return target;
};

export const objFromQueryString = (str = location.search.slice(1)) => {
  return str.split("&").reduce((acc, part) => {
    const [key, value] = part.split("=", 2).map(decodeURIComponent);
    if (key != null && value != null) {
      acc[key] = value;
    }

    return acc;
  }, {});
};

export const objToQueryString = (obj) => {
  return Object.keys(obj)
    .filter((key) => obj[key] != null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join("&");
};

/**
 * Given a `Promise` that should resolve with a `Response` (e.g. a `Promise` returned by `fetch`), returns
 * a new Promise that will reject if 1) the original `Promise` rejects, 2) the original `Promise` resolves
 * with something that's not a `Response`, or 3) the original response had an error status.
 */
const wrapResponsePromise = (responsePromise) => {
  return new Promise((resolve, reject) => {
    return responsePromise
      .then((response) => {
        if (!(response instanceof Response && response.ok)) {
          reject(response);
          return;
        }

        resolve(response);
      })
      .catch(reject);
  });
};

/**
 * Like `fetch` but returns a `Promise` that rejects if `!response.ok`. This allows calling code to handle
 * non-ok responses (e.g. 404s and 500s) using `Promise.catch`.
 */
export const errorSensitiveFetch = (...args) =>
  wrapResponsePromise(fetch(...args));

export const postJSON = (url, json, options) => {
  return makeJSONRequest(url, json, "POST", options);
};

export const patchJSON = (url, json, options) => {
  return makeJSONRequest(url, json, "PATCH", options);
};

/**
 * Sends a `fetch` request to `url` using the given `method` (e.g. "GET", "POST", "PATCH") and returns a
 * `Promise` that resolves with a `Response`. If `options.rejectOnErrorResponse === true`, the returned
 * `Promise` will reject if the sever returns an error response.
 */
function makeJSONRequest(url, json, method, options = {}) {
  const { rejectOnErrorResponse = false } = options;
  const fetchPromise = fetch(url, {
    body: JSON.stringify(json),
    credentials: "same-origin",
    headers: { "Content-Type": "application/json; charset=utf-8" },
    method,
  });
  if (!rejectOnErrorResponse) {
    return fetchPromise;
  }
  return wrapResponsePromise(fetchPromise);
}

export const ping = (url) => {
  const img = new Image();
  img.src = url;
};

// Polls a `url` for up to `timeoutMS`. Assumes the response is a JSON object
// with keys { state, value } where the values for `state` are one of
// ["success", "failed", "timeout"].
export const poll = (url, timeoutMS) => {
  let timedOut = false;
  setTimeout(() => (timedOut = true), timeoutMS);

  const request = () => {
    if (timedOut) {
      return Promise.reject(new Error("poll exceeded timeout duration"));
    }

    return fetch(url, { credentials: "same-origin" })
      .then((response) => response.json())
      .then(({ state, value }) => {
        switch (state) {
          case "success":
            return value;
          case "failed":
            throw new Error(
              `poll received failed response with value: ${value}`
            );
          case "timeout":
            return request();
          default:
            throw new Error(
              `poll received response with unknown state: ${state}`
            );
        }
      });
  };

  return request();
};

export const precisionRound = (number, precision) => {
  const factor = Math.pow(10, precision);

  return Math.round(number * factor) / factor;
};

export const randomInt = (low, high) => {
  return low + Math.floor(Math.random() * (high - low));
};

// Returns an array of integers from [start, stop) (i.e. exclusive of stop).
// Special case: if start === stop, return array with start as the only
// element.
export const range = (start, stop, step = 1) => {
  if (start === stop) return [start];

  if (stop - start < 0 !== step < 0) {
    throw new Error(`Invalid call to range: range(${start}, ${stop}, ${step})`);
  }

  return Array(((stop - start) / step) | 0)
    .fill(null)
    .map((_, i) => start + i * step);
};

export const request = (method, url, headers = {}, body = null) => {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.onreadystatechange = () => {
      if (req.readyState === 4 && req.status === 200) {
        resolve(req);
      }
    };
    req.addEventListener("load", resolve.bind(null, req));
    req.addEventListener("error", reject.bind(null, req));
    req.open(method.toUpperCase(), url);
    Object.keys(headers).forEach((key) => {
      req.setRequestHeader(key, headers[key]);
    });
    req.send(body);
  });
};

export const runAsync = (fn) => {
  setTimeout(fn, 0);
};

export const removeKeys = (source, keys) => {
  if (source == null) return {};

  return Object.keys(source)
    .filter((key) => !keys.includes(key))
    .reduce((target, key) => {
      target[key] = source[key];

      return target;
    }, {});
};

/** Remove keys if their value is empty.  */
export const removeEmptyKeys = (source) => {
  return selectKeys(source, (_, val) => !isEmpty(val));
};

export const selectKeys = (source, keys) => {
  if (source == null) return {};

  switch (type(keys)) {
    case "array":
      return keys.reduce((target, key) => {
        if (source.hasOwnProperty(key)) {
          target[key] = source[key];
        }

        return target;
      }, {});
    case "function":
      return Object.keys(source).reduce((target, key) => {
        if (keys(key, source[key])) {
          target[key] = source[key];
        }

        return target;
      }, {});
  }
};

/**
 * Reorders items randomly into a new Array.
 * @param {Iterable} arr
 * @returns {Array}
 */
export const shuffle = (arr) => {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Like `Array.sort()`, but maintains the relative order of array items with
// equal sort values and does not mutate the original array.
export const stableSort = (arr, sortFn) => {
  const index = arr.reduce((agg, item, i) => {
    agg.set(item, i);

    return agg;
  }, new Map());

  return arr
    .slice()
    .sort((a, b) => sortFn(a, b) || index.get(a) - index.get(b));
};

export const timeDifference = (a, b) => {
  const msPerSecond = 1000,
    msPerMinute = msPerSecond * 60,
    msPerHour = msPerMinute * 60,
    msPerDay = msPerHour * 24,
    msPerWeek = msPerDay * 7,
    msPerMonth = msPerWeek * 4,
    msPerYear = msPerMonth * 13,
    elapsed = Math.abs(a - b);
  if (a <= b && elapsed < 1000 * 10) {
    return "just now";
  } else {
    let val, units;
    if (elapsed < msPerMinute) {
      val = Math.round(elapsed / msPerSecond);
      units = "second";
    } else if (elapsed < msPerHour) {
      val = Math.round(elapsed / msPerMinute);
      units = "minute";
    } else if (elapsed < msPerDay) {
      val = Math.round(elapsed / msPerHour);
      units = "hour";
    } else if (elapsed < msPerWeek) {
      val = Math.round(elapsed / msPerDay);
      units = "day";
    } else if (elapsed < msPerMonth) {
      val = Math.round(elapsed / msPerWeek);
      units = "week";
    } else if (elapsed < msPerYear) {
      val = Math.round(elapsed / msPerMonth);
      units = "month";
    } else {
      val = Math.round(elapsed / msPerYear);
      units = "year";
    }

    return [
      val,
      `${units}${val === 1 ? "" : "s"}`,
      a < b ? "ago" : "from now",
    ].join(" ");
  }
};

export const type = (object) => {
  return {}.toString
    .call(object)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase();
};

/** Returns a Promise that resolves after `ms` ms. */
export const wait = (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

/** Returns a Promise that rejects after `ms` ms. */
export const timeout = (ms) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Promise timed out after ${ms}ms`)), ms);
  });
};

export const unique = (arr) => {
  return arr.filter((el, i) => arr.indexOf(el) === i);
};

export const isFunction = (object) => type(object) === "function";



// WEBPACK FOOTER //
// ./src/js/frontendlib/utils.js